import { action, computed, observable, runInAction } from 'mobx';

import api from 'core/util/api';

import Collection from 'core/model/Collection';
import { RuleTestState } from 'app/stores/interface/RuleTestState';
import $auth from 'app/stores/$auth';
import DeviceModel from 'app/stores/device/DeviceModel';
import RuleModel from 'app/stores/interface/RuleModel';
import {
  DeviceCount,
  InterfaceJson,
  InterfaceSettingsJson,
  isCompleteType,
  RuleCount,
  RuleInterface
} from 'app/stores/interface/types';
import RuleState from 'app/stores/interface/RuleState';
import {
  DeviceTestInterfaceClassificationReturn,
  RuleJson,
  V3ClassificationTestResponse
} from '@kentik/ui-shared/types/interfaceClassification';
import InterfaceClassCollection from './interfaceClass/InterfaceClassCollection';
import InterfacesDeviceCollection from './InterfacesDeviceCollection';
import InterfaceCollection from './InterfaceCollection';
import InterfaceSettings from './InterfaceSettings';
import RuleCollection from './RuleCollection';

interface CompanyAndDeviceIds {
  company_id: string | number;
  device_id: string | number;
}

interface AutoClassifyResults {
  devices: {
    device_id: string;
    results: { autoClassified: unknown[]; autoClassifiedCount: number }[];
  }[];
}

class InterfaceClassStore {
  /**
   * Appears to be unused?
   */
  private collection = new InterfaceClassCollection();

  /**
   * Flag indicating whether changes have been made to the rules (enabled/disabled, reordered, etc.)
   * that require the user to trigger a new backend evaluation run.
   */
  @observable
  public isRuleEvaluationRequired = false;

  /**
   * Flag indicating whether the primary classification data (rules, devices, aggregate counts)
   * has successfully finished its initial load from the backend.
   */
  @observable
  public isClassificationDataReady = false;

  /**
   * Flag indicating whether the manual classification overrides have been fetched and processed.
   */
  @observable
  private isManualClassificationDataReady = false;

  /**
   * The global total number of active interfaces that currently have no classification.
   */
  @observable
  public unclassifiedInterfaceCount = 0;

  /**
   * The global total number of valid interfaces across all active devices in the system.
   */
  @observable
  public totalInterfaceCount = 0;

  /**
   * Manages the persistent, server-side state of rule evaluations.
   * Tracks the progress of background classification runs.
   */
  public ruleState?: RuleState;

  /**
   * Manages the temporary, ephemeral state of a rule currently being actively tested or edited in the UI.
   * Handles the live WebSocket stream for previewing rule changes.
   */
  public ruleTestState?: RuleTestState;

  /**
   * Collection containing all the saved classification rules configured for the company.
   */
  public ruleCollection = new RuleCollection();

  /**
   * The primary collection of devices used to populate the main "Devices" tab.
   */
  public deviceCollection = new InterfacesDeviceCollection();

  /**
   * Collection used to temporarily hold interface models when drilling down into a
   * specific device's matches inside the Rule Editor.
   */
  private testResultsInterfaceCollection = new InterfaceCollection();

  /**
   * A secondary collection of devices specifically used in the Rule Editor modal.
   * This receives temporary match counts and sorts on the fly to power the live rule previews.
   */
  public testResultsDeviceCollection = new InterfacesDeviceCollection();

  /**
   * Model representing the global interface classification settings
   * (e.g., default network boundaries, global exclusion rules).
   */
  public settings = new InterfaceSettings();

  /**
   * Internal collection holding the flat list of all unclassified interfaces.
   * Lazy-loaded over the network only when the "Unclassified Interfaces" tab is opened.
   */
  public allUnclassifiedInterfacesCollection = new InterfaceCollection([], { groupBy: 'device_name' });

  private manualDeviceCollection = new InterfacesDeviceCollection();

  @observable
  public isFetchingActiveRuleInterfaces = false;

  private activeRuleInterfacesCollection = new InterfaceCollection();

  @observable
  private isFetchingDeviceInterfaces = false;

  private settingsFetchPromise: null | Promise<InterfaceSettingsJson> = null;

  @observable
  private hasLoadedSettings = false;

  @observable.ref
  public v3TestResults: V3ClassificationTestResponse | null = null;

  @observable
  public v3IsTesting = false;

  /**
   * Fetches the interfaces belonging to a particular rule (because of the rest of $interfaceClass this is expected to
   * be the "active" rule in view)
   */
  @action
  public fetchActiveRuleInterfaces(ruleId: number): Promise<void> {
    this.isFetchingActiveRuleInterfaces = true;
    this.activeRuleInterfacesCollection.reset();

    return this.getDetailedRuleInterfaces(ruleId)
      .then(this.updateRuleInterfaces)
      .finally(() =>
        runInAction(() => {
          this.isFetchingActiveRuleInterfaces = false;
        })
      );
  }

  /**
   * Processes the raw detailed rule interfaces from the backend, flattens them, appends
   * device names, and injects match counts into the device collection models so they can be sorted.
   *
   * @param ruleInterfaces - Raw interface payload returned from the API.
   */
  @action private updateRuleInterfaces = (ruleInterfaces: RuleInterface[]) => {
    const flattenedInterfaces = ruleInterfaces.map(({ interface_details, device_id }) => ({
      ...interface_details,
      device_id,
      device_name: this.deviceCollection.deviceIdNameHash[device_id]
    }));

    this.activeRuleInterfacesCollection.set(flattenedInterfaces);

    // memoize matchCountsByDevice inside the device collection
    const matchCountsByDevice = flattenedInterfaces.reduce(
      (acc, { device_id }) => {
        acc[device_id] = (acc[device_id] ?? 0) + 1;
        return acc;
      },
      {} as Record<string, number>
    );

    this.testResultsDeviceCollection.each((device) => {
      // we need to update all devices to ensure that we have zeroed out data from other rules
      device.set('activeRuleMatchCount', matchCountsByDevice[device.id] || 0);
    });
  };

  @action
  public initialize() {
    this.ruleState = new RuleState({
      onSuccess: this.handleSuccess,
      deviceCollection: this.deviceCollection,
      ruleCollection: this.ruleCollection,
      settings: this.settings
    });

    this.ruleTestState = new RuleTestState({
      onSuccess: this.handleTestSuccess,
      deviceCollection: this.testResultsDeviceCollection,
      settings: this.settings
    });
  }

  /**
   * Callback fired by the WebSocket listener when a temporary, unsaved rule test evaluation finishes.
   *
   * @param data - Payload indicating the completion status of the test run.
   */
  public handleTestSuccess = (data: unknown) => {
    if (isCompleteType(data)) {
      this.updateFromTestResults();
    }
  };

  /**
   * Intercepts live WebSocket evaluation results from an unsaved test rule, computes
   * device and classification matches, and immediately forces UI collections to update
   * for a real-time preview experience.
   *
   * TODO I don't think it actually _is_ realtime right now though and anyway that might be too expensive to run
   */
  @action
  private updateFromTestResults() {
    if (!this.ruleTestState?.rule) {
      return;
    }
    const testRule = this.ruleTestState.rule;

    const ruleId = testRule.id || testRule.optimisticId;

    const matchedInterfaces: InterfaceJson[] = [];
    const matchCountsByDevice: Record<string, number> = {};
    const classifiedCountsByDevice: Record<string, number> = {};
    let globalTestClassifiedCount = 0;

    // evaluationResults contains the temporary WebSocket results streamed from the backend
    this.ruleTestState.evaluationResults?.forEach((result) => {
      const { device_id, interfaces = [] } = result;

      let deviceMatchCount = 0;
      let deviceClassifiedCount = 0;

      interfaces.forEach((intf) => {
        // Determine the total number of devices/interfaces classified
        const isClassified =
          (intf.connectivity_type !== intf.initial_connectivity_type &&
            intf.network_boundary !== intf.initial_network_boundary) ||
          intf.rules_matched?.some((rule) => rule.didClassify);
        if (isClassified) {
          deviceClassifiedCount += 1;
        }

        // Determine whether this specific interface was classified by this specific rule
        const matchedOurRule = intf.rules_matched?.some(
          (r) =>
            String(r.id) === String(ruleId) || (testRule.optimisticId && String(r.id) === String(testRule.optimisticId))
        );
        if (matchedOurRule) {
          deviceMatchCount += 1;
          matchedInterfaces.push({
            ...intf,
            device_id,
            device_name: this.deviceCollection.deviceIdNameHash[device_id]
          });
        }
      });

      globalTestClassifiedCount += deviceClassifiedCount;
      matchCountsByDevice[device_id] = deviceMatchCount;
      classifiedCountsByDevice[device_id] = deviceClassifiedCount;
    });

    this.activeRuleInterfacesCollection.set(matchedInterfaces);

    // Update the device models
    this.testResultsDeviceCollection.each((device) => {
      const matched = matchCountsByDevice[device.id] || 0;
      const classified = classifiedCountsByDevice[device.id] || 0;
      const total = (device.get('interfaceCount') as number) || 1; // Prevent NaN / 0

      device.set({
        activeRuleMatchCount: matched,
        interfacesClassifiedCount: classified,
        percentMatched: classified / total
      });
    });

    if (this.totalInterfaceCount > 0) {
      this.ruleTestState.totalPercentClassified = globalTestClassifiedCount / this.totalInterfaceCount;
    } else {
      this.ruleTestState.totalPercentClassified = 0;
    }

    testRule.set('interfacesClassifiedCount', matchedInterfaces.length);

    this.testResultsDeviceCollection.sort();
  }

  /**
   * The primary initialization flow. Kicks off fetches for devices, rules, settings, and aggregate
   * UI counts. Sorts collections when finished and checks if rule evaluations are required.
   *
   * @param deviceId - Optional device ID to automatically open its detail view upon load.
   */
  @action
  public initClassificationCollections(deviceId?: string | number) {
    this.isClassificationDataReady = false;

    Promise.all([
      this.deviceCollection.fetch(),
      this.getDeviceInterfaceCounts(),
      this.getInterfacesMatchedPerRule(),
      this.initManualClassificationCollections(),
      this.ruleCollection.fetch(),
      this.ensureSettingsLoaded()
    ]).then(([devices, deviceCounts, ruleCounts]) => {
      this.deviceCollection.filter(undefined, {
        customFilter: (model) => model.get('device_status') === 'V'
      });
      // @ts-expect-error TODO this will throw an error if deviceCollection has no entries!
      this.testResultsDeviceCollection.set(this.testResultsDeviceCollection.deserialize(devices));

      this.updateDeviceInterfaceCounts(deviceCounts);

      this.updateRuleCounts(ruleCounts);

      this.isClassificationDataReady = true;

      if (this.ruleCollection.size > 0 && deviceCounts.length === 0) {
        this.evaluateRules();
      } else {
        this.ruleState?.setEvaluationComplete(true);
      }

      if (deviceId) {
        const device = this.deviceCollection.get(deviceId);
        if (device) {
          this.toggleDeviceInterfaceDetails(device);
        }
      }
    });
  }

  public ensureSettingsLoaded = () => {
    if (this.hasLoadedSettings) {
      return Promise.resolve(this.settings);
    }

    if (this.settings.requestStatus === 'fetching' && this.settingsFetchPromise) {
      return this.settingsFetchPromise;
    }

    this.settingsFetchPromise = this.settings.fetch().then(
      (result) => {
        runInAction(() => {
          this.hasLoadedSettings = true;
          this.settingsFetchPromise = null;
        });
        return result;
      },
      (error) => {
        runInAction(() => {
          this.settingsFetchPromise = null;
        });
        throw error;
      }
    );

    return this.settingsFetchPromise;
  };

  private getSetting = (settingName: string, defaultValue: unknown) => {
    const val = this.settings.get(settingName);

    if (val !== undefined) {
      return val;
    }

    return defaultValue;
  };

  @computed
  public get excludeNoDescriptionEnabled() {
    return !!this.getSetting('include_not_no_description_only', false);
  }

  @computed
  public get excludeNoIpEnabled() {
    return !!this.getSetting('excl_no_ip', false);
  }

  /**
   * Initializes counts strictly for manually classified interfaces from the base database table,
   * bypassing any classification test results.
   */
  @action
  private initManualClassificationCollections = (): Promise<void> => {
    this.isManualClassificationDataReady = false;

    return Promise.all([
      api.get('/api/ui/interfaces/rules/classifiedCounts'),
      this.manualDeviceCollection.fetch()
    ]).then(([classifiedCounts]) => {
      Object.keys(classifiedCounts).forEach((deviceId) => {
        const device = this.manualDeviceCollection.get(deviceId);
        if (device) {
          device.set('classifiedCount', classifiedCounts[deviceId].classifiedCount || 0);
          device.set('interfaceCount', classifiedCounts[deviceId].interfaceCount || 0);
        }
      });
      this.manualDeviceCollection.sort();
      this.isManualClassificationDataReady = true;
    });
  };

  /**
   * Processes the device interface counts i.e. the number of classified devices associated with each device.
   */
  @action
  private updateDeviceInterfaceCounts(deviceCounts: DeviceCount[] = []) {
    let total = 0;
    let classified = 0;

    deviceCounts.forEach((dc) => {
      const device = this.deviceCollection.get(dc.device_id);
      if (device) {
        total += parseInt(String(dc.total_interfaces), 10) || 0;
        classified += parseInt(String(dc.classified_interfaces), 10) || 0;

        [this.deviceCollection, this.testResultsDeviceCollection].forEach((collection) => {
          collection.get(dc.device_id)?.set({
            interfaceCount: parseInt(String(dc.total_interfaces), 10) || 0,
            interfacesClassifiedCount: parseInt(String(dc.classified_interfaces), 10) || 0,
            percentMatched: parseFloat(dc.classified_ratio || '0')
          });
        });
      }
    });

    // sort criteria just updated, we need to sort again
    this.deviceCollection.sort();
    this.testResultsDeviceCollection.sort();

    this.totalInterfaceCount = total;
    this.unclassifiedInterfaceCount = total - classified;
    this.allUnclassifiedInterfacesCollection.hasFetched = false;

    if (this.ruleState) {
      this.ruleState.ruleMatchCount = classified;
    }
  }

  /**
   * Updates rules with the number of interfaces classified by each rule
   */
  @action
  private updateRuleCounts(ruleCounts: RuleCount[] = []) {
    ruleCounts.forEach((rc) => {
      const rule = this.ruleCollection.get(rc.rule_id);
      if (rule) {
        rule.set('interfacesClassifiedCount', parseInt(String(rc.total_interfaces_count) || '0', 10));
      }
    });
  }

  /**
   * @returns false if not exceeding the limit, null if still loading, otherwise returns
   * the configured limit so that it can be displayed to the user.
   */
  @computed
  public get cannotShowUnclassifiedInterfaces(): false | null | number {
    if (!this.isClassificationDataReady) {
      return null;
    }
    return this.unclassifiedInterfaceCount > 1000 ? 1000 : false;
  }

  /**
   * Loads and processes the list of all interfaces that are unclassified
   */
  @action
  public fetchUnclassifiedInterfacesData(): Promise<void> {
    if (this.allUnclassifiedInterfacesCollection.hasFetched) {
      return Promise.resolve();
    }

    /**
     * We do NOT show unclassified interfaces if there are more than 1000. Why?
     * - The current UX for unclassified interfaces is not searchable, making it essentially useless when dealing with large numbers of interfaces
     * - More importantly though: we can't convert that many interfaces to InterfaceModels without crashing the tab via OOM.
     * In future we might revisit this, either by adding pagination to IC interfaces or by moving away from Mobx for displaying this data.
     */
    if (this.cannotShowUnclassifiedInterfaces) {
      // Ensure we mark as fetched to avoid getting stuck in a loop (note: this might cause issues if not handled elsewhere!)
      this.allUnclassifiedInterfacesCollection.hasFetched = true;
      return Promise.resolve();
    }

    return this.getUnclassifiedInterfaces().then((interfaces) => {
      const decorated = interfaces
        // drop interfaces that belong to devices which have been deleted (TODO this should really be happening in the backend?)
        .filter((intf) => this.deviceCollection.get(intf.device_id))
        .map((intf) => ({
          ...intf,
          device_name: this.deviceCollection.deviceIdNameHash[intf.device_id]
        }));
      this.allUnclassifiedInterfacesCollection.set(decorated);
      this.allUnclassifiedInterfacesCollection.hasFetched = true;
    });
  }

  /**
   * Completely clears and resets all collections, caches, and states related to rule classification.
   */
  @action
  public resetRuleStates() {
    this.isClassificationDataReady = false;
    this.ruleCollection.reset();
    this.deviceCollection.reset();
    this.testResultsDeviceCollection.reset();
    this.ruleState?.reset();
    this.allUnclassifiedInterfacesCollection.reset();
    this.allUnclassifiedInterfacesCollection.hasFetched = false;
  }

  /**
   * Clears selections and active evaluation data related specifically to temporary Rule tests.
   */
  @action
  public resetRuleTestStates() {
    this.testResultsDeviceCollection.clearSelection();
    this.testResultsInterfaceCollection.reset();
    this.ruleTestState?.reset();
  }

  /**
   * New version of testClassify, this uses a single API call to do the entire test
   */
  @action
  testClassifyV3 = async (
    rule: RuleModel,
    values: Partial<RuleJson>,
    singleDeviceId: string | number | null = null
  ) => {
    if (!this.ruleTestState) {
      throw new Error('Tried to test classification before initializing $interfaceClass store!');
    }
    this.v3IsTesting = true;
    this.v3TestResults = null;

    rule.isTested = true;

    let ruleClone = rule;
    if (!rule.isNew) {
      ruleClone = rule.duplicate({ removeId: false, save: false });
    }
    ruleClone.safeSet(values);
    this.ruleTestState.rule = ruleClone;

    this.testResultsDeviceCollection.each((device: DeviceModel) => {
      device.set('loadingInterfaceResults', true);
      device.set('activeRuleMatchCount', 0);
    });

    const testRulePayload: RuleJson = {
      ...rule.toJS(),
      ...values,
      match_clause: {
        ...rule.get('match_clause'),
        ...(values.match_clause || {})
      }
    };

    try {
      this.handleV3TestClassificationResponse(
        await api.post('/api/ui/interfaceClassification/test', {
          data: {
            rule: testRulePayload,
            singleDeviceId
          }
        })
      );
    } catch (error) {
      runInAction(() => {
        this.v3TestResults = {
          success: false,
          interfaceMatches: {},
          error: error && typeof error === 'object' && 'message' in error ? error.message : null
        };
        this.testResultsDeviceCollection.each((device: DeviceModel) => {
          device.set('loadingInterfaceResults', false);
        });
      });
      console.error('Fast Path Error:', error);
    } finally {
      // ensure we clean up test results, whether succeed or fail
      runInAction(() => {
        this.v3IsTesting = false;
      });
    }
  };

  @action private handleV3TestClassificationResponse(results: V3ClassificationTestResponse) {
    this.v3TestResults = results;

    if (results.success) {
      this.testResultsDeviceCollection.each((device: DeviceModel) => {
        const dc = results.interfaceMatches[device.id];
        if (dc) {
          const totalInterfaces = (device.get('interfaceCount') as number) || 1;
          device.safeSet({
            activeRuleMatchCount: dc.matchedCount,
            interfacesClassifiedCount: dc.classifiedInterfaces,
            percentMatched: dc.classifiedInterfaces / totalInterfaces,
            loadingInterfaceResults: false
          });
        } else {
          device.set('loadingInterfaceResults', false);
        }
      });

      this.testResultsDeviceCollection.sortState = { field: 'activeRuleMatchCount', direction: 'desc' };
      this.testResultsDeviceCollection.sort();
    }
  }

  /**
   * Processes an array of interfaces for a single device, hydrating them into an InterfaceCollection.
   * Automatically applies temporary test rule edits to the array if a rule is actively being tested.
   *
   * @param device - The Device Model that owns these interfaces.
   * @param interfaces - The array of interface objects to process.
   */
  @action private handleDeviceInterfacesLoaded(device: DeviceModel, interfaces: InterfaceJson[]) {
    const interfaceCollection = new InterfaceCollection(interfaces);
    interfaceCollection.hasFetched = true;

    if (this.ruleTestState && this.ruleTestState.rule) {
      const ruleCollection = new RuleCollection(this.ruleCollection.toJS());
      if (this.ruleTestState.rule.isNew) {
        ruleCollection.add([this.ruleTestState.rule.toJS()]);
        // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
        ruleCollection.att(ruleCollection.size - 1)!.optimisticId = this.ruleTestState.rule.optimisticId;
      } else {
        const existingRule = ruleCollection.get(this.ruleTestState.rule.id);
        if (existingRule) {
          existingRule.set(this.ruleTestState.rule.toJS());
        }
      }

      interfaceCollection.activeRule = this.ruleTestState.rule;
      interfaceCollection.ruleCollection = ruleCollection;
      interfaceCollection.sortState = { field: 'isClassifiedByActiveRule', direction: 'desc' };
      interfaceCollection.sort();
    }

    interfaceCollection.each((model) => model.set('device_id', device.get('id')));
    device.safeSet({ interfaceCollection });
    device.select();
  }

  /**
   * Expands or collapses the detailed interface view for a given device.
   * Automatically intercepts live WebSocket preview data if active, or falls back to querying the DB.
   *
   * @param device - The Device model to toggle details for.
   */
  @action
  public toggleDeviceInterfaceDetails = async (device: DeviceModel) => {
    try {
      if (device.isSelected) {
        this.deviceCollection.clearSelection();
        this.testResultsDeviceCollection.clearSelection();
        // TODO possibly not the correct type?
        const interfaceCollection = device.get('interfaceCollection') as InterfaceCollection;
        if (interfaceCollection) {
          interfaceCollection.reset();
          interfaceCollection.ruleCollection = null;
        }
      } else {
        this.isFetchingDeviceInterfaces = true;

        let interfaces;

        if (this.v3TestResults) {
          // v3 API
          const res: { interfaces: DeviceTestInterfaceClassificationReturn[] } = await api.get(
            `/api/ui/interfaceClassification/devices/${device.id}`,
            {
              query: {
                // we do NOT want to send optimistic IDs for test rules, only when editing an existing rule
                id: this.ruleTestState?.rule?.isNew ? undefined : this.ruleTestState?.rule?.id,
                position_index:
                  this.ruleTestState?.rule?.get('position_index') || this.ruleTestState?.rule?.get('rank'),
                placement: this.ruleTestState?.rule?.get('placement')
              }
            }
          );
          interfaces = res.interfaces;
        } else {
          // legacy API
          const testResult = this.ruleTestState?.evaluationResults?.find(
            (r) => String(r.device_id) === String(device.id)
          );
          interfaces = testResult?.interfaces
            ? testResult.interfaces
            : await api.get(`/api/ui/interfaces/rules/results/device/${device.id}`);
        }

        // Hydrate the collection and open the dialog
        this.handleDeviceInterfacesLoaded(device, interfaces);
      }
    } finally {
      this.isFetchingDeviceInterfaces = false;
    }
  };

  /**
   * Callback fired when a full backend IC process completes.
   * Triggers an update of all system counts and clears caches so new data is fetched.
   */
  public handleSuccess = (data: unknown) => {
    if (isCompleteType(data)) {
      Promise.all([this.getDeviceInterfaceCounts(), this.getInterfacesMatchedPerRule()]).then(
        ([deviceCounts, ruleCounts]) => {
          this.updateDeviceInterfaceCounts(deviceCounts);
          this.updateRuleCounts(ruleCounts);

          this.allUnclassifiedInterfacesCollection.hasFetched = false;

          this.ruleState?.setEvaluationComplete(true);
        }
      );
    }
  };

  /**
   * Persists rule state, triggering a full evaluation of all active rules against all devices.
   */
  @action
  public evaluateRules = () => {
    this.isRuleEvaluationRequired = false;
    this.deviceCollection.models.forEach((model) => model.set({ loadingInterfaceResults: true }));
    this.ruleState?.send({ persist: true });
  };

  /**
   * Tests a single rule. `values` are the new values we want to test, and have not yet
   * been persisted to `rule: BaseModel`
   *
   * @param {RuleModel} rule - The rule being tested.
   * @param {object} values - The new/unsaved attributes of the rule being previewed.
   */
  @action
  public testRule = (rule: RuleModel, values: object) => {
    if (!this.ruleTestState) {
      throw new Error('Attempted to test a rule prior to initialization!');
    }
    // The rule has been tested, we toggle some UI state based on this in the form.
    rule.isTested = true;

    // Take a clone of the rule and persist the temporary tested values but only if it's not new.
    // If it's new, there's no original state to maintain and it loses its optimistic ID so things don't match.
    let ruleClone = rule;
    if (!rule.isNew) {
      ruleClone = rule.duplicate({ removeId: false, save: false }) as RuleModel;
    }
    ruleClone.set(values);
    this.ruleTestState.rule = ruleClone;

    // construct our data to send to the socket, with the values we want to override.
    this.ruleTestState.send({
      persist: false,
      overrideRules: [{ id: rule.id, enabled: true, ...values }]
    });
  };

  /**
   * Changes the rank/priority of a rule and posts the reorder to the backend.
   *
   * @param oldIndex - The original index of the rule in the collection.
   * @param newIndex - The new desired index.
   */
  @action
  public reorderRule = (oldIndex = 0, newIndex = 0) => {
    const rule = this.ruleCollection.at(oldIndex);
    // prevent re-ordering of KMRs
    // TODO I don't think this is the right way to check kentik_managed_rule!
    if (rule && !rule.kentik_managed_rule && oldIndex !== newIndex) {
      this.ruleCollection.move(oldIndex, newIndex);
      const data = { id: rule.id, position_index: newIndex - 1 };
      api.post('/api/ui/interfaces/rules/reorder', { data }).then(() => this.ruleCollection.fetch({ force: true }));
      this.setRuleEvaluationRequired();
    }
  };

  /**
   * Forges a new unsaved rule and makes it the active test rule so it can be edited.
   *
   * @param options - Options payload, normally configuring starting rank.
   */
  public addRule = (options: { isSettingRank?: boolean; rank?: number } = {}) => {
    const { isSettingRank = false, rank = this.ruleCollection.models.length } = options;
    const rule = this.ruleCollection.forge({ isSettingRank, rank });
    this.setActiveTestRule(rule);
  };

  /**
   * Permanently deletes a rule from the backend and flags an evaluation run if needed.
   *
   * @param rule - The rule to remove.
   */
  @action
  public removeRule = (rule: RuleModel): Promise<void> =>
    rule.destroy().then(() => {
      this.ruleCollection.fetch({ force: true });
      if (this.ruleCollection.size === 0) {
        this.evaluateRules();
      } else {
        this.isRuleEvaluationRequired = true;
      }
    });

  /**
   * Prepares a rule to be tested/edited by safely cloning it into the temporary testing state.
   *
   * @param {RuleModel} rule - The rule model to isolate for testing.
   */
  @action
  public setActiveTestRule = (rule: RuleModel) => {
    if (!this.ruleTestState || !this.ruleState) {
      throw new Error('Tried to set active test rule with no rule state available!');
    }
    this.ruleTestState.rule = rule.duplicate({ removeId: false, save: false });
    this.ruleTestState.evaluationResults = this.ruleState.evaluationResults.slice();
    // copy totalPercentClassified from global to ensure that it is propagated
    this.ruleTestState.totalPercentClassified = this.totalPercentClassified;
  };

  /**
   * Flips the enabled/disabled state of a model
   *
   * @param rule - The rule to toggle.
   */
  @action
  public toggleRuleEnabled = (rule: RuleModel) => {
    const { enabled } = rule.get();
    rule.save({ enabled: !enabled }, { toast: false });
    this.setRuleEvaluationRequired();
  };

  /**
   * Flags the UI to prompt the user that rule changes were made and a new evaluation is pending.
   */
  @action
  public setRuleEvaluationRequired() {
    this.isRuleEvaluationRequired = true;
  }

  /**
   * Computes whether the UI is currently in a loading state, either because devices
   * are actively fetching, or because a background rule evaluation is incomplete.
   */
  @computed
  public get isLoadingDevicesInterfaces(): boolean {
    return (
      this.deviceCollection.isRequestActive('fetching') || (!this.ruleState?.complete && !this.ruleState?.evaluating)
    );
  }

  /**
   * Retrieves the default network boundary associated with a given connectivity type.

   * @returns The default network boundary string.
   */
  public getDefaultNetworkBoundary(connectivity_type: string): string {
    return this.settings.get(connectivity_type) as string;
  }

  /**
   * Submits explicit, manual interface classification overrides directly to the backend.
   *
   * @param data - Payload representing the classification override.
   */
  @action
  public classifyInterfaces = (data: object): Promise<object> =>
    api.post('/api/ui/interfaces/classify/manual', { data });

  public autoClassify = ({ company_id, device_id }: CompanyAndDeviceIds): Promise<{ devices: Collection }> =>
    api
      .get('/api/ui/interfaces/classify/auto', { query: { company_id, device_id } })
      .then((results: AutoClassifyResults) => {
        const devices = results.devices.map((device) => ({
          ...device,
          id: device.device_id,
          results: new Collection(device.results, { sortState: { field: 'autoClassified', direction: 'desc' } })
        }));
        return {
          ...results,
          devices: new Collection(devices, { sortState: { field: 'autoClassifiedCount', direction: 'desc' } })
        };
      });

  /**
   * Computes the global total sum of valid interfaces across all active devices in the system.
   */
  @computed
  public get totalInterfaces() {
    if (this.totalInterfaceCount) {
      return this.totalInterfaceCount;
    }
    let total = 0;
    this.manualDeviceCollection.each((model) => {
      total += (model.get('interfaceCount') as number) || 0;
    });
    return total;
  }

  /**
   * Computes the total number of devices in the system that possess at least one interface.
   */
  @computed
  public get totalDevicesWithInterfaces() {
    return (
      this.deviceCollection.unfiltered.filter((device) => ((device.get('interfaceCount') as number) || 0) > 0)
        ?.length ?? 0
    );
  }

  /**
   * Computes the global total sum of interfaces that have been classified (either by rule or manually).
   */
  @computed
  public get totalClassified() {
    if (this.totalInterfaceCount) {
      return this.totalInterfaceCount - this.unclassifiedInterfaceCount;
    }
    let total = 0;
    this.manualDeviceCollection.each((model) => {
      total += (model.get('classifiedCount') as number) || 0;
    });
    return total;
  }

  /**
   * Computes the overall percentage of interfaces across the system that have been successfully classified.
   *
   * @returns Value from 0 to 100.
   */
  @computed
  public get totalPercentClassified(): number {
    return this.totalInterfaces === 0 ? 0 : this.totalClassified / this.totalInterfaces;
  }

  /**
   * Computes whether the overall system classification percentage has fallen below
   * the threshold warning level (defaults to 0.75 but can vary by company)
   */
  @computed
  public get interfaceClassBelowThreshold(): boolean {
    return !!(this.ruleState?.complete && this.totalPercentClassified < $auth.interfaceClassThreshold);
  }

  /**
   * API call to retrieve the lightweight device classification aggregate numbers (totals, classified counts).
   */
  private getDeviceInterfaceCounts(): Promise<DeviceCount[]> {
    return api.get('/api/ui/interfaces/rules/deviceInterfaceCounts');
  }

  /**
   * API call to retrieve the aggregate count of how many interfaces were matched per Rule ID.
   */
  private getInterfacesMatchedPerRule(): Promise<RuleCount[]> {
    return api.get('/api/ui/interfaces/rules/interfacesMatchedPerRule');
  }

  /**
   * API call to extract the flat array of all unclassified interfaces in the system.
   */
  private getUnclassifiedInterfaces(): Promise<{ device_id: string }[]> {
    return api.get('/api/ui/interfaces/rules/unclassifiedInterfaces');
  }

  /**
   * API call to extract the full detailed interface objects that specifically match a given rule.
   */
  private getDetailedRuleInterfaces(ruleId: number): Promise<RuleInterface[]> {
    return api.get(`/api/ui/interfaces/rules/results/${ruleId}`);
  }
}

const $interfaceClass = new InterfaceClassStore();

export default $interfaceClass;
